import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Mail, 
  Phone, 
  MapPin,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { apiService } from '../services/api';
import { Employee, Store } from '../types';
import Swal from 'sweetalert2';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: between;
  margin-bottom: 32px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 24px;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const PageTitle = styled.h1`
  font-size: 28px;
  color: #333;
  margin: 0;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #00d4aa;
  color: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background: #00b894;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
`;

const StoreFilter = styled.select`
  padding: 12px 16px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #00d4aa;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const EmployeeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const EmployeeCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const EmployeeHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const EmployeeInfo = styled.div`
  flex: 1;
`;

const EmployeeName = styled.h3`
  font-size: 18px;
  color: #333;
  margin: 0 0 4px 0;
  font-weight: 600;
`;

const EmployeeRole = styled.span`
  color: #6c757d;
  font-size: 14px;
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const EmployeeDetails = styled.div`
  margin-bottom: 16px;
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 14px;
  color: #666;

  &:last-child {
    margin-bottom: 0;
  }
`;

const EmployeeActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
`;

const ActionButton = styled.button<{ $variant?: 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  flex: 1;
  justify-content: center;

  ${props => {
    if (props.$variant === 'danger') {
      return `
        background: #dc3545;
        color: white;
        &:hover { background: #c82333; }
      `;
    }
    if (props.$variant === 'secondary') {
      return `
        background: #6c757d;
        color: white;
        &:hover { background: #545b62; }
      `;
    }
    return `
      background: #00d4aa;
      color: white;
      &:hover { background: #00b894; }
    `;
  }}
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 64px 20px;
  color: #6c757d;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  margin: 0 0 8px 0;
  color: #495057;
`;

const EmptyStateText = styled.p`
  margin: 0;
  font-size: 14px;
`;

const LoadingState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 64px 20px;
  color: #6c757d;
`;

export const EmployeesPage: React.FC = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStore, setSelectedStore] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [employeesData, storesData] = await Promise.all([
        apiService.getEmployees(),
        apiService.getStores()
      ]);

      if (employeesData.success && Array.isArray(employeesData.data)) {
        setEmployees(employeesData.data);
      }

      if (storesData.success && Array.isArray(storesData.data)) {
        setStores(storesData.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (employeeId: number) => {
    try {
      const response = await apiService.toggleEmployeeStatus(employeeId);
      if (response.success) {
        await loadData(); // Recarrega os dados
      }
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleDelete = async (employeeId: number) => {
    const result = await Swal.fire({
      title: 'Confirmar exclusão',
      text: 'Tem certeza que deseja excluir este funcionário?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        const response = await apiService.deleteEmployee(employeeId);
        if (response.success) {
          await loadData(); // Recarrega os dados
        }
      } catch (error) {
        console.error('Erro ao excluir funcionário:', error);
      }
    }
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStore = !selectedStore || employee.store_id.toString() === selectedStore;
    return matchesSearch && matchesStore;
  });

  const getStoreName = (storeId: number) => {
    const store = stores.find(s => s.id === storeId);
    return store ? store.name : 'Loja não encontrada';
  };

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={16} />
          Voltar
        </BackButton>
        <PageTitle>Funcionários</PageTitle>
        <AddButton onClick={() => navigate('/employees/new')}>
          <Plus size={16} />
          Novo Funcionário
        </AddButton>
      </Header>

      <ControlsBar>
        <SearchContainer>
          <SearchIcon>
            <Search size={16} />
          </SearchIcon>
          <SearchInput
            type="text"
            placeholder="Buscar funcionários..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>

        <StoreFilter
          value={selectedStore}
          onChange={(e) => setSelectedStore(e.target.value)}
        >
          <option value="">Todas as lojas</option>
          {stores.map(store => (
            <option key={store.id} value={store.id}>
              {store.name}
            </option>
          ))}
        </StoreFilter>
      </ControlsBar>

      <EmployeeGrid>
        {loading ? (
          <LoadingState>Carregando funcionários...</LoadingState>
        ) : filteredEmployees.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>Nenhum funcionário encontrado</EmptyStateTitle>
            <EmptyStateText>
              {searchTerm || selectedStore 
                ? "Tente ajustar os filtros de busca"
                : "Comece adicionando seu primeiro funcionário"
              }
            </EmptyStateText>
          </EmptyState>
        ) : (
          filteredEmployees.map(employee => (
            <EmployeeCard key={employee.id}>
              <EmployeeHeader>
                <EmployeeInfo>
                  <EmployeeName>{employee.name}</EmployeeName>
                  <EmployeeRole>Funcionário</EmployeeRole>
                </EmployeeInfo>
                <StatusBadge $active={employee.is_active}>
                  {employee.is_active ? 'Ativo' : 'Inativo'}
                </StatusBadge>
              </EmployeeHeader>

              <EmployeeDetails>
                <DetailItem>
                  <Mail size={14} />
                  {employee.email}
                </DetailItem>
                {employee.phone && (
                  <DetailItem>
                    <Phone size={14} />
                    {employee.phone}
                  </DetailItem>
                )}
                <DetailItem>
                  <MapPin size={14} />
                  {getStoreName(employee.store_id)}
                </DetailItem>
              </EmployeeDetails>

              <EmployeeActions>
                <ActionButton onClick={() => navigate(`/employees/${employee.id}/edit`)}>
                  <Edit size={12} />
                  Editar
                </ActionButton>
                <ActionButton 
                  $variant="secondary"
                  onClick={() => handleToggleStatus(employee.id)}
                >
                  {employee.is_active ? <ToggleLeft size={12} /> : <ToggleRight size={12} />}
                  {employee.is_active ? 'Desativar' : 'Ativar'}
                </ActionButton>
                <ActionButton 
                  $variant="danger"
                  onClick={() => handleDelete(employee.id)}
                >
                  <Trash2 size={12} />
                  Excluir
                </ActionButton>
              </EmployeeActions>
            </EmployeeCard>
          ))
        )}
      </EmployeeGrid>
    </PageContainer>
  );
};
